import { AfterViewInit, ChangeDetectionStrategy, Component, inject, Input, OnChanges, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCalendar, MatDatepickerModule } from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { addMonths, endOfMonth, format, getDay, parseISO, startOfMonth } from 'date-fns';

const WEEKDAY = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];

@Component({
    selector: 'absence-interval-calendar',
    templateUrl: './absence-interval-calendar.component.html',
    styleUrls: ['./absence-interval-calendar.component.scss'],
    imports: [MatButtonModule, MatDatepickerModule, MatIconModule, TranslocoModule],
    changeDetection: ChangeDetectionStrategy.Eager,
    encapsulation: ViewEncapsulation.None
})
export class AbsenceIntervalCalendarComponent implements OnChanges, AfterViewInit {

    @Input() from = '';
    @Input() to = '';
    @Input() weekDays: string[] = [];
    @Input() canceledDates: string[] = [];

    @ViewChild(MatCalendar) calendar: MatCalendar<Date>;

    activeDate = new Date();
    rangeStart = startOfMonth(new Date());
    rangeEnd = endOfMonth(new Date());

    private transloco = inject(TranslocoService);
    private followedFrom = '';

    dateClass = (date: Date): string | null => {
        const bounds = this.bounds();
        if (!bounds) {
            return null;
        }
        const iso = format(date, 'yyyy-MM-dd');
        if (iso < bounds.start || iso > bounds.end) {
            return null;
        }
        if (this.canceledDates?.includes(iso)) {
            return 'absence-canceled';
        }
        if (this.weekDays?.length && !this.weekDays.includes(WEEKDAY[getDay(date)])) {
            return 'absence-excluded';
        }
        return 'absence-applies';
    };

    ngOnChanges(): void {
        const from = this.iso(this.from);
        if (from && this.monthKey(from) !== this.monthKey(this.followedFrom)) {
            this.activeDate = startOfMonth(parseISO(from));
        }
        this.followedFrom = from;
        this.clampMonth();
        this.syncCalendar();
    }

    ngAfterViewInit(): void {
        this.syncCalendar();
    }

    monthLabel(): string {
        const label = new Intl.DateTimeFormat(this.transloco.getActiveLang() || 'es-ES', {
            month: 'long',
            year: 'numeric'
        }).format(this.activeDate);
        return label.charAt(0).toUpperCase() + label.slice(1);
    }

    hasWeekDays(): boolean {
        return !!this.weekDays?.length;
    }

    hasCanceled(): boolean {
        const bounds = this.bounds();
        if (!bounds) {
            return false;
        }
        return (this.canceledDates ?? []).some(day => day >= bounds.start && day <= bounds.end);
    }

    canGoPrevious(): boolean {
        const bounds = this.bounds();
        if (!bounds) {
            return false;
        }
        return startOfMonth(this.activeDate).getTime() > startOfMonth(parseISO(bounds.start)).getTime();
    }

    canGoNext(): boolean {
        const bounds = this.bounds();
        if (!bounds) {
            return false;
        }
        return startOfMonth(this.activeDate).getTime() < startOfMonth(parseISO(bounds.end)).getTime();
    }

    previousMonth(): void {
        this.activeDate = addMonths(startOfMonth(this.activeDate), -1);
        this.syncCalendar();
    }

    nextMonth(): void {
        this.activeDate = addMonths(startOfMonth(this.activeDate), 1);
        this.syncCalendar();
    }

    ignoreSelection(): void {
        this.syncCalendar();
    }

    private bounds(): { start: string, end: string } | null {
        const from = this.iso(this.from);
        if (!from) {
            return null;
        }
        const to = this.iso(this.to) || from;
        return to < from ? { start: to, end: from } : { start: from, end: to };
    }

    private clampMonth(): void {
        const bounds = this.bounds();
        if (!bounds) {
            return;
        }
        const start = startOfMonth(parseISO(bounds.start));
        const end = endOfMonth(parseISO(bounds.end));
        this.rangeStart = start;
        this.rangeEnd = end;
        const current = startOfMonth(this.activeDate);
        if (current.getTime() < start.getTime()) {
            this.activeDate = start;
        } else if (current.getTime() > startOfMonth(end).getTime()) {
            this.activeDate = startOfMonth(end);
        }
    }

    private syncCalendar(): void {
        const calendar = this.calendar;
        if (!calendar) {
            return;
        }
        calendar.minDate = this.rangeStart;
        calendar.maxDate = this.rangeEnd;
        calendar.activeDate = this.activeDate;
        const monthView = calendar.monthView;
        if (monthView) {
            monthView.minDate = this.rangeStart;
            monthView.maxDate = this.rangeEnd;
            monthView.activeDate = this.activeDate;
        }
        calendar.updateTodaysDate();
    }

    private iso(value: string): string {
        return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value) ? value.substring(0, 10) : '';
    }

    private monthKey(iso: string): string {
        return iso ? iso.substring(0, 7) : '';
    }
}
